import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../../services/elearningApi';
import { Module, Course, ModuleContent, Slide, Quiz } from '../../../types/elearning';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import SlideBuilder from '../../../components/elearning/admin/SlideBuilder';
import QuizBuilder from '../../../components/elearning/admin/QuizBuilder';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { useToast } from '../../../hooks/useToast';
import ToastContainer from '../../../components/ui/ToastContainer';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { moveItem, ReorderHandle } from '../../../components/elearning/admin/reorder.tsx';

export default function ModuleEditor() {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const navigate = useNavigate();
  const isNew = moduleId === 'new';

  // Form state - define first
  const [title, setTitle] = useState('');
  usePageTitle(isNew ? 'New Module' : (title || 'Edit Module'));
  const [slug, setSlug] = useState('');
  const [estimatedTime, setEstimatedTime] = useState(15);
  const [content, setContent] = useState<ModuleContent>({ slides: [] });
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [expandedSlides, setExpandedSlides] = useState<Set<string>>(new Set());
  const [editingSlideTitle, setEditingSlideTitle] = useState<string | null>(null);
  const draggingSlideId = useRef<string | null>(null);
  const [dropSlideId, setDropSlideId] = useState<string | null>(null);

  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<string>('');
  const { toasts, success, error: showError, removeToast } = useToast();

  // Track initial data for comparison (only when editing and module is loaded)
  useEffect(() => {
    if (!isNew && module && module.id && !lastSavedData) {
      const initialData = JSON.stringify({
        title: module.title,
        slug: module.slug,
        estimatedTime: module.estimatedTime,
        content: module.content,
      });
      setLastSavedData(initialData);
    }
  }, [isNew, module?.id]); // Only run when module ID changes

  // Check for unsaved changes (only when editing)
  useEffect(() => {
    if (!isNew && lastSavedData) {
      const currentData = JSON.stringify({
        title,
        slug,
        estimatedTime,
        content,
      });
      setHasUnsavedChanges(currentData !== lastSavedData);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [title, slug, estimatedTime, content, lastSavedData, isNew]);

  const { handleNavigation } = useUnsavedChanges({
    hasUnsavedChanges,
    message: 'You have unsaved changes. Are you sure you want to leave?',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch course
        const courseData = await elearningAPI.fetchCourseById(courseId!);
        if (!courseData) {
          setError(new Error('Course not found'));
          setLoading(false);
          return;
        }
        setCourse(courseData);

        // Fetch module if editing
        if (!isNew && moduleId) {
          // Find module in course
          const moduleData = courseData.modules?.find((m) => m.id === moduleId);
          if (!moduleData) {
            setError(new Error('Module not found'));
            setLoading(false);
            return;
          }
          setModule(moduleData);
          setTitle(moduleData.title);
          setSlug(moduleData.slug);
          setEstimatedTime(moduleData.estimatedTime);
          setContent(moduleData.content);
          setQuiz(moduleData.quiz || null);
        }
        // For new modules, loading is done after course is fetched
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load data'));
        setLoading(false);
      }
    };

    if (courseId) {
      fetchData();
    } else {
      // No courseId, set loading to false
      setLoading(false);
    }
  }, [courseId, moduleId, isNew]);

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const handleTitleBlur = () => {
    // Only auto-generate slug on blur if slug is empty and we have a title
    if (!slug && title.trim()) {
      setSlug(generateSlug(title));
    }
  };

  const handleAddSlide = () => {
    // Collapse all existing slides
    setExpandedSlides(new Set());
    
    const slideNumber = content.slides.length + 1;
    const newSlide: Slide = {
      id: `slide-${Date.now()}`,
      order: slideNumber,
      title: `Slide ${slideNumber}`,
      components: [],
    };
    setContent({
      ...content,
      slides: [...content.slides, newSlide],
    });
    
    // Auto-expand the new slide
    setExpandedSlides(new Set([newSlide.id]));
  };

  const handleSlideTitleChange = (slideId: string, newTitle: string) => {
    // Trim the title and use empty string if empty (will show default on display)
    const trimmedTitle = newTitle.trim();
    setContent({
      ...content,
      slides: content.slides.map((s) =>
        s.id === slideId ? { ...s, title: trimmedTitle || undefined } : s
      ),
    });
  };

  const toggleSlide = (slideId: string) => {
    setExpandedSlides((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(slideId)) {
        newSet.delete(slideId);
      } else {
        newSet.add(slideId);
      }
      return newSet;
    });
  };

  const handleDropSlide = (targetId: string) => {
    const fromId = draggingSlideId.current;
    setDropSlideId(null);
    if (!fromId || fromId === targetId) return;
    setContent((current) => {
      const next = moveItem(current.slides, fromId, targetId);
      if (next === current.slides) return current;
      return {
        ...current,
        slides: next.map((slide, index) => ({ ...slide, order: index + 1 })),
      };
    });
  };

  const handleRemoveSlide = (slideId: string) => {
    setContent({
      ...content,
      slides: content.slides.filter((s) => s.id !== slideId).map((s, index) => ({
        ...s,
        order: index + 1,
      })),
    });
  };

  const handleUpdateSlide = (slideId: string, updatedSlide: Slide) => {
    setContent({
      ...content,
      slides: content.slides.map((s) => (s.id === slideId ? updatedSlide : s)),
    });
  };

  const handleDelete = async () => {
    if (!moduleId || isNew || !courseId) return;
    if (!confirm(`Delete module "${title.trim() || 'this module'}"? This cannot be undone.`)) return;
    try {
      setSaving(true);
      await elearningAPI.deleteModule(moduleId);
      navigate(`/elearning/admin/courses/${courseId}/edit`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete module');
      setSaving(false);
    }
  };

  const handleSave = async () => {
    console.log('Save button clicked', { title, content, isNew, courseId, moduleId });
    
    if (!title.trim()) {
      showError('Please enter a module title');
      return;
    }

    // Allow saving even with empty slides - content can be added later
    // if (content.slides.length === 0) {
    //   alert('Please add at least one slide to the module');
    //   return;
    // }

    try {
      setSaving(true);
      console.log('Saving module...', { title, content, estimatedTime, isNew, moduleId, module });

      // Determine if we're creating or updating
      // Use moduleId from params, or fall back to module state ID
      const actualModuleId = moduleId || module?.id;
      const isActuallyNew = isNew || !actualModuleId;

      if (isActuallyNew) {
        if (!courseId) {
          throw new Error('Course ID is required');
        }
        
        const moduleData = {
          courseId: courseId,
          title: title.trim(),
          slug: slug.trim() || generateSlug(title),
          order: (course?.modules?.length || 0) + 1,
          content,
          estimatedTime,
        };
        
        console.log('Creating module with data:', moduleData);
        const newModule = await elearningAPI.createModule(courseId, moduleData);
        console.log('Module created:', newModule);
        
        // Update local state
        setModule(newModule);
        setTitle(newModule.title);
        setSlug(newModule.slug);
        setContent(newModule.content);
        setEstimatedTime(newModule.estimatedTime);
        
        // Update last saved data
        setLastSavedData(JSON.stringify({
          title: newModule.title,
          slug: newModule.slug,
          estimatedTime: newModule.estimatedTime,
          content: newModule.content,
        }));
        setHasUnsavedChanges(false);
        
        // Refresh course data to get updated modules list
        const updatedCourse = await elearningAPI.fetchCourseById(courseId);
        if (updatedCourse) {
          setCourse(updatedCourse);
        }
        
        // Navigate to edit page with the new module ID
        navigate(`/elearning/admin/courses/${courseId}/modules/${newModule.id}/edit`, { replace: true });
        success('Module created successfully!');
      } else if (actualModuleId) {
        console.log('Updating module:', actualModuleId);
        const updated = await elearningAPI.updateModule(actualModuleId, {
          title: title.trim(),
          slug: slug.trim() || generateSlug(title),
          content,
          estimatedTime,
        });
        console.log('Module updated:', updated);
        
        // Update local state
        setModule(updated);
        
        // Update last saved data
        setLastSavedData(JSON.stringify({
          title: title.trim(),
          slug: slug.trim() || generateSlug(title),
          estimatedTime,
          content,
        }));
        setHasUnsavedChanges(false);
        
        // Refresh course data
        if (courseId) {
          const updatedCourse = await elearningAPI.fetchCourseById(courseId);
          if (updatedCourse) {
            setCourse(updatedCourse);
            const updatedModule = updatedCourse.modules?.find(m => m.id === actualModuleId);
            if (updatedModule) {
              setModule(updatedModule);
            }
          }
        }
        
        success('Module saved successfully!');
      } else {
        throw new Error('Unable to determine module ID for saving');
      }
    } catch (err) {
      console.error('Error saving module:', err);
      showError(err instanceof Error ? err.message : 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // For new modules, ensure we have course data before rendering
  if (isNew && !course) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState 
          error={new Error('Course not found')} 
          onRetry={() => window.location.reload()} 
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'eLearning', to: '/elearning' },
          {
            label: course?.title || 'Course',
            to: courseId ? `/elearning/admin/courses/${courseId}/edit` : '/elearning',
          },
          { label: isNew ? 'New Module' : 'Edit Module' },
        ]}
      />

      <Link
        to={`/elearning/admin/courses/${courseId}/edit`}
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Course</span>
      </Link>

      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isNew ? 'Create New Module' : 'Edit Module'}
          </h1>
          <p className="text-gray-600">
            {isNew
              ? 'Add a new module to this course'
              : 'Update module content and settings'}
          </p>
        </div>
        {!isNew && course?.slug && module?.slug && (
          <Link
            to={`/elearning/courses/${course.slug}/modules/${module.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
          >
            <EyeIcon className="h-5 w-5" />
            Preview as learner
          </Link>
        )}
      </div>

      {/* Module Metadata */}
      <Card className="mb-6">
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Module Title *
            </label>
            <Input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={handleTitleBlur}
              placeholder="e.g., Introduction to Blood Types"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL Slug
              </label>
              <Input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="auto-generated-from-title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Time (minutes)
              </label>
              <Input
                type="number"
                value={estimatedTime}
                onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 0)}
                min={1}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Slide Builder */}
      <Card className="mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Module Content</h2>
              {content.slides.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {content.slides.length} {content.slides.length === 1 ? 'slide' : 'slides'} •{' '}
                  {content.slides.reduce((acc, slide) => acc + slide.components.length, 0)} total components
                </p>
              )}
            </div>
            <Button variant="primary" onClick={handleAddSlide}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Slide
            </Button>
          </div>

          {content.slides.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
              <p className="mb-2 font-medium">No slides yet</p>
              <p className="text-sm mb-4">Add your first slide to start building module content</p>
              <Button variant="primary" onClick={handleAddSlide}>
                <PlusIcon className="h-5 w-5 mr-2" />
                Add First Slide
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {content.slides.map((slide, index) => {
                const isExpanded = expandedSlides.has(slide.id);
                return (
                  <div
                    key={slide.id}
                    onDragOver={(event) => {
                      if (content.slides.length < 2) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      if (dropSlideId !== slide.id) setDropSlideId(slide.id);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      handleDropSlide(slide.id);
                    }}
                    onDragLeave={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                      if (dropSlideId === slide.id) setDropSlideId(null);
                    }}
                    className={`border border-gray-200 rounded-lg hover:border-gray-300 transition-colors ${
                      dropSlideId === slide.id && draggingSlideId.current !== slide.id
                        ? 'ring-2 ring-primary'
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-3 flex-1">
                        {content.slides.length > 1 && (
                          <ReorderHandle
                            label={`Drag to reorder ${slide.title || `Slide ${index + 1}`}`}
                            onDragStart={(event) => {
                              draggingSlideId.current = slide.id;
                              event.dataTransfer.effectAllowed = 'move';
                              event.dataTransfer.setData('text/plain', slide.id);
                            }}
                            onDragEnd={() => {
                              draggingSlideId.current = null;
                              setDropSlideId(null);
                            }}
                          />
                        )}
                        <button
                          onClick={() => toggleSlide(slide.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          aria-label={isExpanded ? 'Collapse slide' : 'Expand slide'}
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                          )}
                        </button>
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingSlideTitle === slide.id ? (
                            <input
                              type="text"
                              value={slide.title || ''}
                              placeholder={`Slide ${index + 1}`}
                              onChange={(e) => handleSlideTitleChange(slide.id, e.target.value)}
                              onBlur={() => {
                                setEditingSlideTitle(null);
                                // Ensure title is saved even if empty (will use default on display)
                                if (!slide.title || slide.title.trim() === '') {
                                  handleSlideTitleChange(slide.id, '');
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                } else if (e.key === 'Escape') {
                                  // Revert to original title on escape
                                  setEditingSlideTitle(null);
                                }
                              }}
                              className="text-sm font-medium text-gray-700 bg-white border border-primary rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary w-full max-w-xs"
                              autoFocus
                            />
                          ) : (
                            <button
                              onClick={() => setEditingSlideTitle(slide.id)}
                              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors text-left"
                              title="Click to edit slide name"
                            >
                              {slide.title || `Slide ${index + 1}`}
                            </button>
                          )}
                          <span className="text-xs text-gray-500 ml-2">
                            {slide.components.length} {slide.components.length === 1 ? 'component' : 'components'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Are you sure you want to delete Slide ${index + 1}? This cannot be undone.`)) {
                              handleRemoveSlide(slide.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="p-4">
                        <SlideBuilder
                          slide={slide}
                          onUpdate={(updatedSlide) => handleUpdateSlide(slide.id, updatedSlide)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Quiz Builder */}
      {isNew ? (
        <Card className="mb-6">
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quiz Builder</h3>
              <p className="text-sm text-gray-600">
                Save the module first to add a quiz. Once saved, you'll be able to create quizzes for this module.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        module && module.id && (
          <QuizBuilder
            moduleId={module.id}
            existingQuiz={quiz || undefined}
            onQuizChange={(updatedQuiz) => {
              setQuiz(updatedQuiz);
              // Update module's hasQuiz flag
              if (updatedQuiz && module) {
                setModule({ ...module, hasQuiz: true, quiz: updatedQuiz });
              } else if (module) {
                setModule({ ...module, hasQuiz: false, quiz: undefined });
              }
            }}
          />
        )
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between gap-3 mt-6 pt-6 border-t border-gray-200">
        <div className="text-sm">
          {hasUnsavedChanges && (
            <span className="text-yellow-600 font-medium flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              You have unsaved changes
            </span>
          )}
          {!hasUnsavedChanges && !isNew && (
            <span className="text-green-600 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              All changes saved
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {moduleId && moduleId !== 'new' && (
            <Button
              variant="ghost"
              className="!px-3 !py-2 !text-error hover:!bg-red-50"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete module
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => handleNavigation(`/elearning/admin/courses/${courseId}/edit`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !title.trim()}
            type="button"
            className={hasUnsavedChanges ? 'ring-2 ring-yellow-500' : ''}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              'Save Module'
            )}
          </Button>
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

